import identity from 'lodash/identity';
import pickBy from 'lodash/pickBy';
import qs from 'qs';

import createBus from '~/support/utils/event-bus';
import { useQueryManager } from '~/composables/useQueryManager';
const DEFAULT_STATE = {
  count: 0,
  endCursor: null,
  hasNextPage: false,
  hasPreviousPage: false,
  page: 1,
  previousEndCursor: '',
  previousPageChange: '',
  previousStartCursor: '',
  rowsPerPage: 10,
  startCursor: null,
};

const PaginationEvents = {
  CHANGE: 'change',
};

const Actions = {
  DECREASE: 'decrease',
  INCREASE: 'increase',
} as const;

type Keys = keyof typeof Actions;
type ActionValues = (typeof Actions)[Keys];

export default function usePagination(id: string) {
  const $bus = createBus();
  const route = useRoute();
  const router = useRouter();
  const { setQueryParams } = useQueryManager();
  const state = reactive<{
    page: number;
    endCursor: string | null;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor: string | null;
    rowsPerPage: number;
    count: number;
  }>(DEFAULT_STATE);
  const lastAction = ref<ActionValues>();

  function nextPage() {
    state.page += 1;
    state.hasPreviousPage = true;
    lastAction.value = Actions.INCREASE;
    emitChange();
  }

  function previousPage() {
    if (state.page === 1) {
      return;
    }

    state.page = state.page - 1;
    lastAction.value = Actions.DECREASE;
    emitChange();
  }

  async function reset() {
    state.page = DEFAULT_STATE.page;
    state.endCursor = DEFAULT_STATE.endCursor;
    state.hasNextPage = DEFAULT_STATE.hasNextPage;
    state.hasPreviousPage = DEFAULT_STATE.hasPreviousPage;
    state.startCursor = DEFAULT_STATE.startCursor;
    state.rowsPerPage = DEFAULT_STATE.rowsPerPage;
    state.count = DEFAULT_STATE.count;
    lastAction.value = undefined;

    await nextTick();
  }

  async function setRowsPerPage(rows: number) {
    state.rowsPerPage = rows;
    state.page = 1;
    lastAction.value = undefined;
    state.startCursor = null;
    state.endCursor;

    emitChange();
  }

  function setStartCursor(cursor: string) {
    state.startCursor = cursor;
  }

  function setEndCursor(cursor: string) {
    state.endCursor = cursor;
    route.query.endCursor = cursor;
  }

  function setCount(count: number) {
    state.count = count;
  }

  function emitChange() {
    $bus.emit(PaginationEvents.CHANGE, { id, ...state, action: lastAction.value });

    const query = pickBy(
      {
        ...route.query,
        action: lastAction.value,
        end: state.endCursor,
        page: state.page,
        rows: state.rowsPerPage,
        start: state.startCursor,
      },
      identity,
    );
    setQueryParams(query);
  }

  function setPageInformationFromResponse(pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    endCursor: string;
    startCursor: string;
  }) {
    if (!pageInfo) {
      return;
    }

    state.hasNextPage = pageInfo.hasNextPage;
    state.hasPreviousPage = pageInfo.hasPreviousPage;
    state.endCursor = pageInfo.endCursor;
    state.startCursor = pageInfo.startCursor;
    state.page = pageInfo.hasPreviousPage ? state.page : 1;
  }

  const paginationArgs = computed(() => {
    switch (lastAction.value) {
      case Actions.INCREASE:
        return {
          after: state.endCursor,
          first: state.rowsPerPage,
        };
      case Actions.DECREASE:
        return {
          before: state.startCursor,
          last: state.rowsPerPage,
        };

      default:
        return {
          first: state.rowsPerPage,
        };
    }
  });

  onMounted(async () => {
    if (!route.path.includes(id)) {
      return;
    }

    if (route.query.start) {
      state.startCursor = route.query.start as string;
    }

    if (route.query.end) {
      state.endCursor = route.query.end as string;
    }

    if (route.query.rows) {
      state.rowsPerPage = Number(route.query.rows);
    }

    if (route.query.page) {
      state.page = Number(route.query.page);
    }

    if (route.query.action) {
      lastAction.value = route.query.action as ActionValues;
    }

    emitChange();
  });

  watch(
    () => id,
    () => {
      reset();
    },
  );

  return {
    args: paginationArgs,
    emits: PaginationEvents,
    events: $bus,
    info: state,
    nextPage,
    previousPage,
    reset,
    setCount,
    setEndCursor,
    setPageInformationFromResponse,
    setRowsPerPage,
    setStartCursor,
  };
}
